import React, { useState } from "react";

import S from "./strings";
import { Ventana, diaSemanaCorto, diaSemanaFecha, hoyYmd, rangoHoras } from "./piezasTaller";

/*
	Attendance of a workshop (Módulos Claros, 2026-11-01). ONE way to take it: the grid student x day in the window
	(desktop), and «Pasar Lista» = the same marks for one day as a window (phone, and the teacher in Asistencias).
	A mark is Vino, Faltó (saved as absent) or nothing yet; future days cannot be marked.
*/

export type Marca = "vino" | "falto" | "nada";

export interface DiaLista { id: number; date: string; startTime: string; endTime: string | null; }
export interface AlumnaLista { attendeeId: number; name: string; code?: string | null; marca: Marca; }

export const marcaDe = (attended: boolean | undefined, marked: boolean | undefined): Marca => attended ? "vino" : marked ? "falto" : "nada";
export const textoMarca = (m: Marca): string => m === "vino" ? S.vino : m === "falto" ? S.falto : "—";

/** The day that «Pasar Lista» opens on: today, or the last day already passed, or the first one. */
export const diaInicial = (dias: DiaLista[]): number | null => {
	if (!dias.length) return null;
	const hoy = hoyYmd();
	const pasados = dias.filter((d) => d.date <= hoy);
	return (pasados.length ? pasados[pasados.length - 1] : dias[0]).id;
};

/** Grid student x day. Clicking a cell: nothing -> Vino -> Faltó -> Vino. */
export const RejillaAsistencia: React.FC<{
	dias: DiaLista[];
	filas: { attendeeId: number; name: React.ReactNode; activa: boolean; marcas: Record<number, Marca>; fin: React.ReactNode }[];
	editable: boolean;
	enCurso: string[];
	finTitulo: string;
	onMarcar: (diaId: number, attendeeId: number, vino: boolean) => void;
}> = ({ dias, filas, editable, enCurso, finTitulo, onMarcar }) => {
	const hoy = hoyYmd();
	return (
		<div className="wtlRejilla">
			<table>
				<thead>
					<tr>
						<th />
						{dias.map((d) => <th key={d.id} className={d.date === hoy ? "hoy" : ""} title={diaSemanaFecha(d.date) + " " + rangoHoras(d.startTime, d.endTime)}>{diaSemanaCorto(d.date)}</th>)}
						<th className="der">{finTitulo}</th>
					</tr>
				</thead>
				<tbody>
					{filas.map((f) => (
						<tr key={f.attendeeId} className={f.activa ? "" : "wtlBaja"}>
							<td>{f.name}</td>
							{dias.map((d) => {
								const m = f.marcas[d.id] || "nada";
								const futuro = d.date > hoy;
								const clave = d.id + "_" + f.attendeeId;
								const label = diaSemanaFecha(d.date) + ": " + textoMarca(m);
								return (
									<td key={d.id} className="cel">
										{editable && f.activa
											? <button type="button" className={"wtlMarca " + m} disabled={futuro || enCurso.includes(clave)} aria-label={label} title={label}
												onClick={() => onMarcar(d.id, f.attendeeId, m !== "vino")}>{textoMarca(m)}</button>
											: <span className={"wtlMarca " + m} title={label}>{textoMarca(m)}</span>}
									</td>
								);
							})}
							<td className="der">{f.fin}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

/** «Pasar Lista»: one day at a time, a big Vino / Faltó per student. */
export const PasarLista: React.FC<{
	titulo: React.ReactNode;
	dias: DiaLista[];
	alumnas: (diaId: number) => AlumnaLista[];
	enCurso: string[];
	error?: string | null;
	soloLectura?: boolean;
	onMarcar: (diaId: number, attendeeId: number, vino: boolean) => void;
	onClose: () => void;
	diaElegido?: number | null;
}> = ({ titulo, dias, alumnas, enCurso, error, soloLectura, onMarcar, onClose, diaElegido }) => {
	const [dia, setDia] = useState<number | null>(diaElegido ?? diaInicial(dias));
	const hoy = hoyYmd();
	const d = dias.find((x) => x.id === dia) || null;
	const lista = d ? alumnas(d.id) : [];
	const vinieron = lista.filter((a) => a.marca === "vino").length;
	const futuro = !!d && d.date > hoy;
	return (
		<Ventana titulo={titulo} onClose={onClose} pie={<button type="button" className="btn btn-primary btn-sm" onClick={onClose}>{S.listo}</button>}>
			{dias.length > 1 ? (
				<div className="wtlDiasChips" role="group" aria-label={S.dias}>
					{dias.map((x) => <button key={x.id} type="button" className="wtlDiaChip wau-num" aria-pressed={x.id === dia} onClick={() => setDia(x.id)}>{x.date === hoy ? S.hoy : diaSemanaCorto(x.date)}</button>)}
				</div>
			) : null}
			{!d ? <div className="wtlSub">{S.sinDias}</div> : <>
				<div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
					<span><b className="wau-num">{diaSemanaFecha(d.date)}</b> <span className="wtlSub" style={{ display: "inline" }}>{rangoHoras(d.startTime, d.endTime)}</span></span>
					<b className="wau-num">{S.vino} {S.presentes(vinieron, lista.length)}</b>
				</div>
				{lista.length === 0 ? <div className="wtlSub">{S.sinInscritas}</div> : (
					<div className="wtlPase">
						{lista.map((a) => {
							const ocupado = enCurso.includes(d.id + "_" + a.attendeeId);
							const off = soloLectura || futuro || ocupado;
							return (
								<div key={a.attendeeId} className="wtlPaseFila">
									<span className="nom"><b>{a.name}</b>{a.code ? <div className="wtlSub">{a.code}</div> : null}</span>
									<span className="wtlPaseBotones">
										<button type="button" className={"vino" + (a.marca === "vino" ? " on" : "")} aria-pressed={a.marca === "vino"} disabled={off}
											onClick={() => a.marca !== "vino" && onMarcar(d.id, a.attendeeId, true)}>{S.vino}</button>
										<button type="button" className={"falto" + (a.marca === "falto" ? " on" : "")} aria-pressed={a.marca === "falto"} disabled={off}
											onClick={() => a.marca !== "falto" && onMarcar(d.id, a.attendeeId, false)}>{S.falto}</button>
									</span>
								</div>
							);
						})}
					</div>
				)}
			</>}
			{error ? <div className="waAviso" role="alert" style={{ marginTop: 10 }}>{error}</div> : null}
		</Ventana>
	);
};
